import React from "react";

import { DataLoadError } from "components/ui/DataLoadError";
import { useAllergies } from "features/patients/hooks/useAllergies";

import styles from "./AllergiesAndIntolerancesAlert.module.css";

// The backend records "nothing to flag" as an allergy row named e.g.
// "No Known Drug Allergies". It is not a warning, so it must not raise a
// warning banner — a patient with only these rows shows nothing at all.
const isNoKnownAllergyRecord = (allergy) =>
  /^no known/i.test((allergy?.name ?? "").trim());

const AllergiesAndIntolerancesAlert = ({ patientId }) => {
  const {
    data: allergies,
    isError,
    isFetching,
    refetch,
  } = useAllergies(patientId);

  // A failed fetch must not leave the banner absent — that reads as
  // "no known allergies" (KIN-1614).
  if (isError) {
    return (
      <DataLoadError
        label="this patient's allergies"
        text="This is not a statement that the patient has none. Check the allergy record before prescribing."
        onRetry={refetch}
        isRetrying={isFetching}
      />
    );
  }

  const flaggable = (allergies ?? []).filter(
    (allergy) => !isNoKnownAllergyRecord(allergy)
  );

  if (!flaggable.length) return null;

  const hasSerious = flaggable.some((a) => a.serious);

  return (
    <div
      className={`${styles.box} ${
        hasSerious ? styles.boxRed : styles.boxYellow
      }`}
    >
      <p
        className={`${styles.title} ${
          hasSerious ? styles.titleRed : styles.titleYellow
        }`}
      >
        Allergies and Intolerances
      </p>
      <p
        className={`${styles.text} ${
          hasSerious ? styles.textRed : styles.textYellow
        }`}
      >
        {flaggable
          .map((a) => (a.serious ? `${a.name} (Serious)` : a.name))
          .join(", ")}
      </p>
    </div>
  );
};

export { AllergiesAndIntolerancesAlert };
